// client/src/hooks/useAdminTool.js
// Custom hook for managing admin tool state and API calls
// Reduces code duplication for database tools (seed, reset, cleanup, etc.)

import { useState, useCallback } from 'react'
import adminToolsService from '../services/AdminToolsService'

// Map kebab-case tool names to camelCase service method names
const toolNameMap = {
  'expire-proposals': 'expireProposals',
  'clear-all': 'clearAll',
  'clear-all-seeds': 'clearAll',
  'seed-large-scale': 'seedLargeScale',
  'reset-large-scale': 'resetLargeScale',
  'demo-seed': 'demoSeed',
  'demo-reset': 'demoReset',
  'demo-register-players': 'demoRegisterPlayers',
  'demo-assign-teams': 'demoAssignTeams',
  'demo-open-proposals': 'demoOpenProposals',
  'demo-accept-proposal': 'demoAcceptProposal',
  'demo-enter-scores': 'demoEnterScores',
  'demo-run-all': 'demoRunAll',
  'skill-demo-seed': 'skillDemoSeed',
  'skill-demo-reset': 'skillDemoReset',
  'skill-demo-run-all': 'skillDemoRunAll',
  seed: 'seed',
  reset: 'reset',
  cleanup: 'cleanup',
}

/**
 * Custom hook for managing admin tool operations
 * @param {string} toolName - The name of the admin tool (e.g., 'seed', 'reset', 'expire-proposals')
 * @param {object} options - Optional configuration
 * @param {function} options.buildRequestBody - Function to build custom request body
 * @returns {object} Tool state and control functions
 */
function useAdminTool(toolName, options = {}) {
  const { buildRequestBody } = options

  const [state, setState] = useState({
    loading: false,
    message: '',
    stdout: '',
    stderr: '',
  })

  const execute = useCallback(
    async (params = {}) => {
      setState({ loading: true, message: '', stdout: '', stderr: '' })
      let body

      try {
        body = buildRequestBody ? buildRequestBody(params) : params

        // Map kebab-case tool name to camelCase method name
        const methodName = toolNameMap[toolName] || toolName

        // Use service instead of direct fetch
        const data = await adminToolsService[methodName](body)

        // Handle different response formats
        let message,
          stdout = '',
          stderr = ''

        if (toolName === 'expire-proposals') {
          // Special handling for expire-proposals response format
          message = data.success
            ? `${data.action || toolName} succeeded: ${data.data?.message || ''}`
            : `${data.action || toolName} failed: ${data.data?.error || ''}`
        } else {
          message = data.success
            ? `${data.action || toolName} succeeded`
            : `${data.action || toolName} failed`
          stdout = data.stdout || ''
          stderr = data.stderr || ''
        }

        setState({
          loading: false,
          message,
          stdout,
          stderr,
        })

        return data
      } catch (err) {
        const responseData = err?.response?.data || {}
        const detailedStderr =
          responseData?.stderr || responseData?.error || err.message

        setState({
          loading: false,
          message: `${toolName} failed`,
          stdout: '',
          stderr: detailedStderr,
        })
        // Don't re-throw - just set the error state like the original implementation
      }
    },
    [toolName, buildRequestBody],
  )

  const reset = useCallback(() => {
    setState({ loading: false, message: '', stdout: '', stderr: '' })
  }, [])

  return {
    ...state,
    execute,
    reset,
  }
}

export default useAdminTool
